import { styled, Typography } from '@mui/material';
import type { AccountDBSource } from '@partful/shared-types';
import { useProductInventoryQuery } from '@partful/shared/lib/hooks/useProductInventoryQuery';
import { useQueryParams } from '@partful/shared/lib/hooks/useQueryParams';
import { GA } from '@services/gtag';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadButton } from 'src/components/Button/DownloadButton';

interface DownloadKitsProps {
  account: string;
  tenant: AccountDBSource;
  productName: string;
  setIsLoading: (isLoading: boolean) => void;
}

export interface GetKitsTemplateResponse {
  downloadURL: string;
}

export interface Overview {
  name: string;
  PID: string;
  version: string;
  url: string;
}

export interface GetKitsResponse {
  overviews: Overview[];
}

export function DownloadKits({ account, tenant, productName, setIsLoading }: Readonly<DownloadKitsProps>) {
  const { t } = useTranslation('manageProduct');
  const kitExample = '/downloads/Partful-Kit-template.csv';

  const searchParams = useQueryParams();
  const { configurationPID } = searchParams;
  const {
    data: kits,
    isLoading: isGetKitsLoading,
    isError: isGetKitsError,
  } = useProductInventoryQuery<GetKitsResponse>('kits', { account: `${account}`, ...searchParams });

  const {
    data: kitTemplate,
    isLoading: isGetKitsTemplateLoading,
    isError: isGetKitsTemplateError,
  } = useProductInventoryQuery<GetKitsTemplateResponse>(`kits-template/${configurationPID}`, { account });

  const hasKits = kits?.overviews && kits?.overviews.length > 0 && !isGetKitsLoading && !isGetKitsError;

  const hasKitsTemplate = kitTemplate && !isGetKitsTemplateLoading && !isGetKitsTemplateError;

  const isLoading = isGetKitsLoading || isGetKitsTemplateLoading;
  useEffect(() => {
    setIsLoading(isLoading);
  }, [isLoading, setIsLoading]);

  const handleDownload = async () => {
    if (hasKits && hasKitsTemplate) {
      // this should be the uploaded kit and not the example
      window.open(kitTemplate.downloadURL, '_blank');
      GA.ManageProductPage.downloadExistingKitsFile(tenant, configurationPID, productName);
    } else {
      GA.ManageProductPage.downloadExampleKitTemplate(tenant, configurationPID, productName);
      window.open(kitExample, '_blank');
    }
  };

  if (hasKits) {
    return (
      <>
        <StepTitle>{t('kits.templates.download.currentKitTitle')}</StepTitle>
        <DownloadButton onClick={handleDownload}>{t('kits.templates.download.downloadKitsDataFile')}</DownloadButton>
      </>
    );
  }

  return (
    <>
      <StepTitle>{t('kits.templates.download.kitsExampleTemplateTitle')}</StepTitle>
      <StepDescription>{t('kits.templates.download.description')}</StepDescription>
      <DownloadButton onClick={handleDownload}>{t('kits.templates.download.downloadExampleFile')}</DownloadButton>
    </>
  );
}

const StepDescription = styled(Typography)`
  margin-bottom: 0.5rem;
`;

const StepTitle = styled('h3')`
  ${({ theme }) => `
    font-size: 1rem;
    font-weight: ${theme.typography.fontWeightBold};
  `}
  margin-bottom: 1rem;
`;
